import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';

import { HaiIcon } from '@/components/icons';

/**
 * HomeOnboarding — 一個專案都沒有時的新手空狀態(D3 `home.js` `secOnboard` / `.ob`)。
 * -----------------------------------------------------------------------------
 * 儀表板那一整套(四張快照卡 / 近期專案 / 每週成效 / 待辦)在零專案時全是空殼,
 * 讓新使用者面對一整片 0 沒有幫助 —— 這裡只留一件事:**建立第一個專案**。
 *
 * D3 幾何:56×56 圓角圖示磚 + 標題 + 一段說明 + 實心 CTA,整組垂直置中
 * (`.ob{min-height:calc(100vh - 140px);display:grid;place-items:center}`)。
 *
 * ⚠️ 進入條件由 `HomePage` 判(`projects.length === 0`);`projects === null` 是
 * **載入中**,不可以走這裡 —— 否則會閃一下空狀態再跳回儀表板。
 */
export interface HomeOnboardingProps {
  /** 問候語要帶的名字(displayName → email 前綴 → i18n「訪客」,與儀表板同一份)。 */
  userName: string;
}

export function HomeOnboarding({ userName }: HomeOnboardingProps) {
  const { t } = useTranslation('home');

  return (
    <div style={wrapStyle} data-testid="home-onboarding">
      <div style={innerStyle}>
        <span style={iconStyle} aria-hidden>
          <HaiIcon name="folder" size={26} />
        </span>
        {/* R402(owner:「會截斷人家的姓名」)— 名字包成一個 inline-block 整塊換行:
            放不下就整個名字移到下一行,不在名字中間斷開(CJK 每個字之間都是斷點,
            英文名在空白斷)。名字長過一整行時才在名字內折行(overflowWrap),不溢出。
            name 是外部資料 → 走 <Trans> 插值(React escape),不拼 HTML。 */}
        <h2 style={titleStyle}>
          <Trans
            t={t}
            i18nKey="onboarding.title"
            values={{ name: userName }}
            components={{ who: <span style={nameStyle} data-testid="home-onboarding-name" /> }}
          />
        </h2>
        <p style={descStyle}>{t('onboarding.desc')}</p>
        {/* D3 `.ob-in .btn-solid`:實心 teal 主行動(全站 `.hai-btn-solid`;左 13px ＋號) */}
        <Link to="/new" className="hai-btn-solid" style={ctaStyle} data-testid="home-onboarding-cta">
          <HaiIcon name="plus" size={13} />
          {t('onboarding.cta')}
        </Link>
      </div>
    </div>
  );
}

const wrapStyle: CSSProperties = {
  minHeight: 'calc(100vh - 140px)',
  display: 'grid',
  placeItems: 'center',
  paddingBottom: 24,
};

/**
 * R402 — 外框放寬到 560(D3 `.ob-in` 是 420):28px 標題「歡迎使用 HAi Creative,」
 * 就佔掉約 340px,420 只剩 ~80px 給名字 = 三個中文字以上就被折斷。
 * 說明段落仍維持 D3 的 420 行寬(`descStyle.maxWidth`),只有標題變寬。
 */
const innerStyle: CSSProperties = {
  maxWidth: 560,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: 8,
};

/** D3 `.ob-ic`:teal-50 底 + teal-600 圖示(--tide-400 在淺底只有 ~1.8:1)。 */
const iconStyle: CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: 14,
  background: 'var(--concept-soft)',
  color: 'var(--tide-600)',
  display: 'grid',
  placeItems: 'center',
  marginBottom: 12,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-2xl)',
  fontWeight: 800,
  color: 'var(--text-strong)',
  letterSpacing: '-0.01em',
};

/** 名字整塊換行(見 JSX 註解);inline-block 的寬度上限 = 一整行,超過才在內部折。 */
const nameStyle: CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
};

const descStyle: CSSProperties = {
  maxWidth: 420,
  margin: 0,
  fontSize: 'var(--text-sm)',
  // D3 `.ob-in p` = var(--sub)(CIS 次文字)
  color: 'var(--ink-500)',
  lineHeight: 1.75,
};

/** 幾何 / 底色 / hover 由 `.hai-btn-solid`(--btn-solid)負責;這裡只補版位與連結底線。 */
const ctaStyle: CSSProperties = {
  marginTop: 12,
  textDecoration: 'none',
};
